/**
 * ChannelIcon · Real brand marks for each sales channel
 * ────────────────────────────────────────────────────────────────────
 * Icon sources:
 *   · SiShopify / SiTiktok / SiEtsy / SiEbay  → react-icons/si (Simple Icons, CC0)
 *   · FaAmazon                                → react-icons/fa (FontAwesome Brands)
 *   · Walmart                                 → inline SVG (Simple Icons dropped
 *     the Walmart mark for trademark reasons; we render a compact 6-ray "spark"
 *     silhouette that reads instantly + Walmart's canonical yellow-on-blue.)
 *
 * Generic channels (no real brand) fall back to Lucide glyphs.
 * Pass `mono` to inherit `currentColor` from the parent.
 */
import { SiShopify, SiTiktok, SiEtsy, SiEbay } from "react-icons/si";
import { FaAmazon } from "react-icons/fa";
import { Code2, Building2, ShoppingBag } from "lucide-react";

const BRAND_COLORS = {
  shopify_dtc: "#96BF47",  // Shopify green
  amazon:      "#FF9900",  // Amazon orange
  tiktok:      "#000000",  // TikTok mark on light bg
  walmart:     "#0071CE",  // Walmart blue
  etsy:        "#F16521",  // Etsy orange
  ebay:        "#E53238",  // eBay red (primary of the 4-color mark)
};

// Compact Walmart "spark" — six rays around a center dot.
function WalmartMark({ className, color = "#FFC220", stroke = "#0071CE" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-label="Walmart">
      <g fill={color} stroke={stroke} strokeWidth="0.6" strokeLinecap="round">
        {[0, 60, 120, 180, 240, 300].map(angle => {
          const rad = (angle * Math.PI) / 180;
          const x1 = 12 + Math.cos(rad) * 3.5;
          const y1 = 12 + Math.sin(rad) * 3.5;
          const x2 = 12 + Math.cos(rad) * 9.5;
          const y2 = 12 + Math.sin(rad) * 9.5;
          return (
            <g key={angle}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="2.6"/>
              <circle cx={x2} cy={y2} r="1.4" fill={color}/>
            </g>
          );
        })}
        <circle cx="12" cy="12" r="1.8" fill={color}/>
      </g>
    </svg>
  );
}

export default function ChannelIcon({ type, className = "w-4 h-4", mono = false }) {
  const color = mono ? "currentColor" : BRAND_COLORS[type];

  switch (type) {
    case "shopify_dtc":
    case "shopify":
      return <SiShopify className={className} color={color}/>;
    case "amazon":
      return <FaAmazon className={className} color={color}/>;
    case "tiktok":
      return <SiTiktok className={className} color={color}/>;
    case "walmart":
      return mono
        ? <WalmartMark className={className} color="currentColor" stroke="currentColor"/>
        : <WalmartMark className={className}/>;
    case "etsy":
      return <SiEtsy className={className} color={color}/>;
    case "ebay":
      return <SiEbay className={className} color={color}/>;
    case "generic_api":
      return <Code2 className={className} strokeWidth={2}/>;
    case "generic_wholesale":
      return <Building2 className={className} strokeWidth={2}/>;
    default:
      return <ShoppingBag className={className} strokeWidth={2}/>;
  }
}
